import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import apiClient from '../../services/api/apiClient.js';

/**
 * Carga masiva de ETA por Excel — portado de `GetDataFromExcelJusTInput`
 * (`IbuFront/src/components/views/EtaUpdate.jsx` en V1, montado dentro del modal
 * `modalETA` de `ListadoContenedores.jsx`).
 *
 * Formato confirmado en V1 (columnas EXACTAS de la primera hoja del .xlsx, leídas con
 * `XLSX.utils.sheet_to_json`, sin mapeo de encabezados alternativos):
 *   - `ID`            -> id de Mongo del Contenedor (columna usada en la URL del PATCH)
 *   - `ETA`           -> se manda tal cual como `etaReal`
 *   - `Observaciones` -> se manda tal cual como `observaciones`
 * V1 hace `PATCH /contenedores/:ID { etaReal, observaciones }` por cada renglón de la
 * PRIMERA hoja del libro (ignora hojas adicionales), disparando todas las peticiones
 * en paralelo (`.map` sin await) y mostrando un `Swal` individual por fila — sin
 * transacción agregada ni contador. Aquí se agrega vista previa + procesamiento
 * SECUENCIAL con contador de éxito/error (mejora explícitamente pedida en el
 * encargo, ver D-BUILD-4.1 en DUDAS.md) mantiene el mismo endpoint/payload/columnas.
 */
export default function EtaMasivaModal({ onClose, onTerminado }) {
  const [filas, setFilas] = useState([]);
  const [nombreArchivo, setNombreArchivo] = useState('');
  const [procesando, setProcesando] = useState(false);
  const [resultado, setResultado] = useState(null); // { ok, error, total }
  const [errorLectura, setErrorLectura] = useState('');

  function leerArchivo(e) {
    const archivo = e.target.files?.[0];
    if (!archivo) return;
    setNombreArchivo(archivo.name);
    setErrorLectura('');
    setResultado(null);

    const reader = new FileReader();
    reader.onloadend = (ev) => {
      try {
        const data = new Uint8Array(ev.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        // Igual que V1: solo se usa la primera hoja del libro.
        const primeraHoja = workbook.SheetNames[0];
        const filasHoja = XLSX.utils.sheet_to_json(workbook.Sheets[primeraHoja]);
        const normalizadas = filasHoja
          .map((f) => ({ id: f.ID, eta: f.ETA, observaciones: f.Observaciones }))
          .filter((f) => f.id);
        setFilas(normalizadas);
        if (!normalizadas.length) {
          setErrorLectura('No se encontraron filas con columna "ID" en la primera hoja del archivo.');
        }
      } catch (err) {
        setErrorLectura('No se pudo leer el archivo. Verifica que sea un .xlsx válido.');
        setFilas([]);
      }
    };
    reader.readAsArrayBuffer(archivo);
  }

  async function aplicar() {
    if (!filas.length) return;
    setProcesando(true);
    setResultado(null);
    let ok = 0;
    let error = 0;
    // Secuencial (no en paralelo como V1) para poder reportar avance/contador sin
    // saturar el backend con N peticiones simultáneas.
    for (const fila of filas) {
      try {
        await apiClient.patch(`/contenedores/${fila.id}`, {
          etaReal: fila.eta,
          observaciones: fila.observaciones,
        });
        ok += 1;
      } catch {
        error += 1;
      }
    }
    setResultado({ ok, error, total: filas.length });
    setProcesando(false);
    onTerminado?.();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h3 className="text-sm font-semibold text-slate-700">Carga masiva de ETA</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-sm">
            Cerrar
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <p className="text-xs text-slate-500 mb-2">
              Sube un archivo .xlsx con las columnas <b>ID</b> (id del contenedor), <b>ETA</b> y{' '}
              <b>Observaciones</b>. Se usa solo la primera hoja del libro.
            </p>
            <input type="file" accept=".xlsx,.xls" onChange={leerArchivo} className="text-sm" />
            {nombreArchivo && <p className="text-xs text-slate-400 mt-1">Archivo: {nombreArchivo}</p>}
            {errorLectura && <p className="text-xs text-danger-500 mt-1">{errorLectura}</p>}
          </div>

          {filas.length > 0 && (
            <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-md">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left">ID</th>
                    <th className="px-2 py-1.5 text-left">ETA nueva</th>
                    <th className="px-2 py-1.5 text-left">Observaciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filas.map((f, i) => (
                    <tr key={`${f.id}-${i}`}>
                      <td className="px-2 py-1.5 font-mono text-xs">{String(f.id)}</td>
                      <td className="px-2 py-1.5">{String(f.eta ?? '')}</td>
                      <td className="px-2 py-1.5">{String(f.observaciones ?? '')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {resultado && (
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm">
              Procesadas {resultado.total} filas — <span className="text-success-600 font-medium">{resultado.ok} correctas</span>
              {resultado.error > 0 && (
                <>
                  {' '}
                  · <span className="text-danger-600 font-medium">{resultado.error} con error</span>
                </>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="btn-outline">
              Cerrar
            </button>
            <button
              type="button"
              disabled={!filas.length || procesando}
              onClick={aplicar}
              className="btn-primary"
            >
              {procesando ? 'Aplicando...' : 'Aplicar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
